import React from "react";
import { styled } from "@mui/system";
import DownArrowIcon from "src/icons/portfolio/DownArrowIcon";
import Collapse from "@mui/material/Collapse";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import ReferAFriendTrustIcon from "src/icons/ReferAFriendTrustIcon";
import { useUserSlice } from "src/store/features/user.slice";
import { Link as RouterLink } from "react-router-dom";

const Wrapper = styled("div")(() => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
}));

const Tile = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  alignSelf: "stretch",
  borderRadius: "4px",
  cursor: "pointer",

  // hover styles for childs
  "&:hover > div": {
    color: "#4E7086",
  },
}));

const HeaderText = styled("div")(() => ({
  flex: "1 0 0",
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

interface ArrowWrapperProps {
  clicked: boolean;
}
const ArrowWrapper = styled("div")<ArrowWrapperProps>(({ clicked }) => ({
  animation: clicked ? "downwards 0.5s forwards" : "leftwards 0.5s forwards",

  // ANIMATION
  "@keyframes leftwards": {
    " 0%": { transform: "rotate(0deg);" },
    "100% ": { transform: "rotate(-90deg)" },
  },

  "@keyframes downwards": {
    " 0%": { transform: "rotate(-90deg);" },
    "100% ": { transform: "rotate(0deg)" },
  },
}));

const NoListWrapper = styled("div")(() => ({
  display: "flex",
  height: "120px",
  justifyContent: "center",
  alignItems: "center",
  alignSelf: "stretch",
}));

const List = styled("ul")(() => ({
  all: "unset",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",
}));

const ListRow = styled("li")(() => ({
  all: "unset",
  display: "flex",
  padding: "16px 12px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "8px",
  background: "#FFF",
}));

const DateAndBalanceWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  alignSelf: "stretch",
}));

const IconWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  alignSelf: "stretch",
}));

const Text = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

const DetailsWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
  alignSelf: "stretch",
}));

const SubText = styled("div")(() => ({
  color: "#7B858B",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
}));

const GreenPlus = styled("span")({
  color: "#0BEC9F",
  paddingRight: "2px",
});

export const UnverifiedLink = styled(RouterLink)({
  color: "#FC4C4C",
  textDecorationLine: "underline",
  padding: "0px 4px",

  "&:hover": {
    color: "#FEDBDB",
  },
});

interface IReferralList {
  id: string;
  timestamp: number;
  reward: string;
  tier: string;
}

interface ReferralListProps {
  list: IReferralList[];
}

/**
 * A component to display a list of referrals.
 * @param {IReferralList[]} props.list - The list of referrals to display.
 */
const ReferralList = (props: ReferralListProps) => {
  const { list } = props;
  const { isUnverified } = useUserSlice();
  const [checked, setChecked] = React.useState(false);

  const handleChange = () => {
    setChecked((prev) => !prev);
  };

  return (
    <Wrapper>
      {/* Header */}
      <Tile id="RaF-referralList-tile" onClick={handleChange}>
        <HeaderText>Your referral rewards</HeaderText>
        <ArrowWrapper clicked={checked}>
          <DownArrowIcon />
        </ArrowWrapper>
      </Tile>

      {/* List */}
      <Collapse in={checked} orientation="vertical" sx={{ width: "100%" }}>
        {list.length <= 0 ? (
          <NoListWrapper id="RaF-noReferralsList">
            {isUnverified ? (
              <Text>
                You have no earnings yet.. Verify your account
                <UnverifiedLink to="/application"> here </UnverifiedLink>
                to begin
              </Text>
            ) : (
              <Text>
                You have no earnings yet.. Make your first referral to & view your rewards here.
              </Text>
            )}
          </NoListWrapper>
        ) : (
          <List id="RaF-referralList">
            {list.map((item) => (
              <ListRow key={item.id}>
                <DateAndBalanceWrapper>
                  <SubText>{dateFormatter(item.timestamp, false, true)}</SubText>
                </DateAndBalanceWrapper>

                <DetailsWrapper>
                  <IconWrapper>
                    <ReferAFriendTrustIcon /> <Text>Referred</Text>
                  </IconWrapper>
                  <Text>
                    <GreenPlus>+</GreenPlus>
                    {item.reward}
                  </Text>
                </DetailsWrapper>
              </ListRow>
            ))}
          </List>
        )}
      </Collapse>
    </Wrapper>
  );
};

export default ReferralList;
